(function () {
  'use strict';

  function options(variant) {
    return variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
  }

  function countOption(product, variant) {
    const values = options(variant);
    const names = (product.options || []).map(option => String(option.name || option).toLowerCase().replace(/[^a-z]/g, ''));
    let index = names.findIndex(name => ['count', 'quantity', 'qty', 'packcount', 'packsize'].includes(name));
    if (index !== -1) {
      const match = String(values[index] || '').match(/^\s*(\d[\d,]*)\s*(?:ct|count|pack|pk|pcs|pieces)?\s*$/i);
      return match ? { index, count: Number(match[1].replace(/,/g, '')), kind: '' } : null;
    }
    // Combined options such as "Small 18ct" must retain their size descriptor.
    index = values.findIndex(value => /\d[\d,]*\s*(?:ct|count)\b/i.test(value));
    if (index === -1) return null;
    const value = String(values[index]);
    const match = value.match(/(\d[\d,]*)\s*(?:ct|count)\b/i);
    return { index, count: Number(match[1].replace(/,/g, '')), kind: value.replace(match[0], '#').trim().toLowerCase() };
  }

  function price(variant, planId) {
    if (!planId) return Number(variant.price);
    const allocation = (variant.selling_plan_allocations || []).find(item => String(item.selling_plan_id) === String(planId));
    return allocation ? Number(allocation.price) : null;
  }

  function defaultVariant(product, initial) {
    const sizeIndex = (product.options || []).findIndex(option => String(option.name || option).toLowerCase().includes('size'));
    const medium = sizeIndex < 0 ? null : (product.variants || []).find(variant => variant.available && String(options(variant)[sizeIndex]).toLowerCase().includes('medium'));
    if (medium) initial = medium;
    const candidates = (product.variants || []).filter(variant => variant.available && countOption(product, variant)?.count === 100);
    return candidates.find(variant => {
      const count = countOption(product, variant);
      const initialCount = initial && countOption(product, initial);
      if (!initial) return false;
      return (!initialCount || count.kind === initialCount.kind) && options(variant).every((value, index) => index === count.index || value === options(initial)[index]);
    }) || (medium ? initial : candidates[0] || initial);
  }

  function findOffer(product, variant, planId = '') {
    if (!product || !variant || !variant.available) return null;
    const current = countOption(product, variant);
    const currentPrice = price(variant, planId);
    if (!current || current.count <= 0 || !(currentPrice > 0)) return null;
    const selected = options(variant);
    const packs = (product.variants || []).map(candidate => {
      if (!candidate.available) return null;
      const next = countOption(product, candidate);
      const nextPrice = price(candidate, planId);
      if (!next || next.index !== current.index || next.kind !== current.kind || next.count <= 0 || !(nextPrice > 0)) return null;
      if (!options(candidate).every((value, index) => index === current.index || value === selected[index])) return null;
      return { variant: candidate, count: next.count, price: nextPrice };
    }).filter(Boolean);
    const next = packs.filter(pack => pack.count > current.count)
      .sort((a, b) => a.count - b.count || a.price - b.price)[0];
    if (!next) return null;
    const additionalCount = next.count - current.count;
    const extra = next.price - currentPrice;
    // Compare the added feeders with the selected pack's exact unit price.
    // A smaller separate pack can have a higher unit price and inflate the claim.
    const referencePrice = additionalCount * currentPrice / current.count;
    const savings = Math.max(0, Math.min(100, Math.floor((1 - extra / referencePrice) * 100 + 1e-9)));
    return { ...next, currentCount: current.count, additionalCount, extra, savings, referencePrice, optionIndex: current.index, planId };
  }

  function escape(value) {
    return String(value).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  }

  function render(offer, money, quantity = 1) {
    if (!offer) return '';
    const packs = Math.max(1, Math.floor(Number(quantity) || 1));
    const count = (offer.count * packs).toLocaleString('en-US');
    // The offer sits beside pack counts, so describe the change to each pack.
    const additionalCount = offer.additionalCount.toLocaleString('en-US');
    const amount = escape(money(offer.price * packs));
    const heading = `${additionalCount} more for ${escape(money(Math.max(0, offer.extra)))}`;
    return `<button type="button" class="product-count-upgrade__link" data-count-upgrade="${escape(offer.variant.id)}" aria-label="Upgrade to ${count} for ${amount}${offer.planId ? ' on this delivery' : ''}">${heading}</button>`;
  }

  function optionPrice(product, selectedOptions, optionIndex, value, planId = '') {
    const variant = (product.variants || []).find(item => options(item).every((option, index) => option === (index === optionIndex ? value : selectedOptions[index])));
    if (!variant) return null;
    const count = countOption(product, variant);
    const amount = price(variant, planId);
    if (!count || count.index !== optionIndex || count.count <= 0 || !(amount > 0)) return null;
    return { total: amount, unit: amount / count.count, label: count.kind ? value : `${count.count.toLocaleString('en-US')} count` };
  }

  function renderOptionPrice(value, money) {
    if (!value) return '';
    return `<span class="product-option-price"><span class="product-option-price__total">${escape(money(value.total))}</span><span class="product-option-price__unit">${escape(money(value.unit))}/ea</span></span>`;
  }

  function updateOptionToggles(root) {
    root.querySelectorAll('.product-option-section').forEach(section => {
      const row = section.querySelector('.product-option-values, .product-card-drawer__option-values');
      const button = section.querySelector('[data-product-options-toggle]');
      if (!row || !button || !row.clientWidth) return;
      // Measure one row even while expanded so its collapse arrow stays available.
      const chips = [...row.children].filter(child => child.matches('.product-option-choice') && !child.hidden);
      const style = getComputedStyle(row);
      const width = chips.reduce((total, chip) => total + chip.getBoundingClientRect().width, 0)
        + Math.max(0, chips.length - 1) * (parseFloat(style.columnGap) || 0)
        + (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      button.hidden = width <= row.clientWidth + 1;
    });
    settleCountOffers(root);
  }

  function alignCountOffers(root) {
    root.querySelectorAll('[data-count-upgrade-slot]').forEach(slot => {
      const button = slot.querySelector('[data-count-upgrade]');
      const section = slot.closest('.product-option-section');
      const selected = section?.querySelector('input:checked + label, .is-selected');
      if (!button || !selected || slot.hidden) return;
      const bounds = slot.getBoundingClientRect();
      const text = selected.querySelector('.product-option-value') || selected;
      const textLeft = text.getBoundingClientRect().left;
      const left = Math.max(bounds.left, Math.min(bounds.right - button.offsetWidth, textLeft));
      const originalLeft = (bounds.left + bounds.right - button.offsetWidth) / 2;
      button.style.transform = `translateX(${left - originalLeft}px)`;
    });
  }

  const offerTimers = new WeakMap();
  function settleCountOffers(root) {
    root.querySelectorAll('[data-count-upgrade-slot]').forEach(slot => {
      const button = slot.querySelector('[data-count-upgrade]');
      if (!button) return;
      button.classList.remove('is-visible');
      clearTimeout(offerTimers.get(slot));
      // Scroll events keep restarting this delay, so text appears only at rest.
      offerTimers.set(slot, setTimeout(() => {
        const row = slot.closest('.product-option-section')?.querySelector('.product-option-values, .product-card-drawer__option-values');
        if (row && optionScrolls.has(row)) return;
        alignCountOffers(root);
        button.classList.add('is-visible');
        offerTimers.delete(slot);
      }, 160));
    });
  }

  const optionScrolls = new WeakMap();
  function stopOptionScroll(row) {
    optionScrolls.get(row)?.();
    return row.scrollLeft;
  }

  function revealSelectedOption(row, animate = true) {
    const selected = row?.querySelector('input:checked + label, .is-selected');
    if (!selected) return;
    stopOptionScroll(row);
    const centeredLeft = selected.offsetLeft + selected.offsetWidth / 2 - row.clientWidth / 2;
    const maxScroll = Math.max(0, row.scrollWidth - row.clientWidth);
    const target = Math.min(maxScroll, Math.max(0, centeredLeft));
    const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const start = row.scrollLeft;
    if (!animate || motion?.matches || Math.abs(target - start) < 1) {
      row.scrollLeft = target;
      return;
    }

    const chips = Array.from(row.children || []).filter(child => child.matches('.product-option-choice') && !child.hidden);
    if (chips.length && chips.every(chip => typeof chip.animate === 'function')) {
      // Set the final scroll position once, then animate the visual offset on
      // the compositor. Price/widget work cannot stall each scroll frame.
      row.scrollLeft = target;
      const distance = row.scrollLeft - start;
      const animations = chips.map(chip => chip.animate([
        { transform: `translateX(${distance}px)` },
        { transform: 'translateX(0)' }
      ], { duration: 260, easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)', fill: 'both' }));
      let active = true;
      const finish = (freeze = false) => {
        if (!active) return;
        active = false;
        // A new tap or swipe starts from what is on screen, not the old target.
        const left = freeze && row.isConnected ? chips[0].getBoundingClientRect().left : null;
        animations.forEach(animation => animation.cancel());
        if (left !== null) row.scrollLeft += chips[0].getBoundingClientRect().left - left;
        ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(type => row.removeEventListener(type, cancel));
        motion?.removeEventListener?.('change', reduce);
        optionScrolls.delete(row);
        const section = row.closest('.product-option-section');
        if (section) settleCountOffers(section);
      };
      const cancel = () => finish(true);
      const reduce = () => { if (motion.matches) finish(); };
      optionScrolls.set(row, cancel);
      ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(type => row.addEventListener(type, cancel, { passive: true }));
      motion?.addEventListener?.('change', reduce);
      // Observe all cancellations so interrupted rows never reject unhandled.
      Promise.all(animations.map(animation => animation.finished)).then(() => finish(), cancel);
      return;
    }

    // Native smooth scrolling may jump on mobile browsers, especially on a
    // freshly rendered drawer row. Retain a timed fallback for older browsers.
    let frame;
    let started;
    const cancel = () => {
      cancelAnimationFrame(frame);
      ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(type => row.removeEventListener(type, cancel));
      optionScrolls.delete(row);
    };
    optionScrolls.set(row, cancel);
    ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(type => row.addEventListener(type, cancel, { passive: true }));
    const step = now => {
      if (!row.isConnected) return cancel();
      started ??= now;
      const progress = motion?.matches ? 1 : Math.min(1, (now - started) / 260);
      row.scrollLeft = start + (target - start) * (1 - Math.pow(1 - progress, 3));
      if (progress < 1) frame = requestAnimationFrame(step);
      else cancel();
    };
    frame = requestAnimationFrame(step);
  }

  function toggleOptionSection(button) {
    const section = button.closest('.product-option-section');
    const expanded = section.classList.toggle('is-expanded');
    button.setAttribute('aria-expanded', String(expanded));
    button.setAttribute('aria-label', `${expanded ? 'Collapse' : 'Show all'} ${button.dataset.optionName} options`);
    const row = section.querySelector('.product-option-values, .product-card-drawer__option-values');
    if (!expanded) revealSelectedOption(row);
  }

  function popupMarkup(offer, money, quantity = 1, fullPopup = false) {
    const packs = Math.max(1, Math.floor(Number(quantity) || 1));
    const count = value => (value * packs).toLocaleString('en-US');
    const extra = offer.extra * packs;
    const currentPrice = offer.price - offer.extra;
    const priceLabel = `${extra < 0 ? 'and save' : 'for'} ${escape(money(Math.abs(extra)))}`;
    const saved = Math.max(0, Math.round((offer.referencePrice - offer.extra) * packs));
    return `<div class="count-upgrade-popup__body">
      <div class="count-upgrade-popup__copy" aria-live="polite">
        <h2>Add ${count(offer.additionalCount)} more ${priceLabel}</h2>
        ${offer.savings > 0 ? `<p class="count-upgrade-popup__savings">Save ${escape(money(saved))} · ${offer.savings}% off</p>` : ''}
      </div>
      ${fullPopup ? `<div class="count-upgrade-popup__comparison">
        <div class="count-upgrade-popup__pack">
          <span class="count-upgrade-popup__pack-label">Current</span>
          <strong class="count-upgrade-popup__pack-count">${count(offer.currentCount)} count</strong>
          <span class="count-upgrade-popup__pack-price">${escape(money(currentPrice * packs))}</span>
          <span class="count-upgrade-popup__pack-unit">${escape(money(currentPrice / offer.currentCount))}/ea</span>
        </div>
        <div class="count-upgrade-popup__pack count-upgrade-popup__pack--upgrade${offer.savings > 0 ? ' is-saving' : ''}">
          <span class="count-upgrade-popup__pack-label">Upgrade</span>
          <strong class="count-upgrade-popup__pack-count">${count(offer.count)} count</strong>
          <span class="count-upgrade-popup__pack-price">${escape(money(offer.price * packs))}</span>
          <span class="count-upgrade-popup__pack-unit">${escape(money(offer.price / offer.count))}/ea</span>
        </div>
      </div>` : ''}
      <button type="button" class="count-upgrade-popup__accept" data-upgrade-accept aria-label="Add ${count(offer.additionalCount)} more ${priceLabel}">${fullPopup ? `Add ${count(offer.additionalCount)} more` : 'Add'}</button>
    </div>`;
  }

  // One popup every seven days per browser, shared across products and return visits.
  // If storage is unavailable, keep the non-interrupting inline experience.
  function claimPopup(storage, now = Date.now()) {
    try {
      storage = storage || window.localStorage;
      const key = 'dubia-count-upgrade-popup-seen-v1';
      const saved = storage.getItem(key);
      if (saved !== null) {
        const lastShown = Number(saved);
        // The old once-ever flag has no date. Start its cooldown without another popup.
        if (lastShown <= 1 || !Number.isFinite(lastShown) || lastShown > now) {
          storage.setItem(key, String(now));
          return false;
        }
        if (now - lastShown < 7 * 24 * 60 * 60 * 1000) return false;
      }
      storage.setItem(key, String(now));
      return true;
    } catch (_) {
      return false;
    }
  }

  let activeModal;
  function showFullPopup(offer, money, { quantity, parent, onAccept }) {
    let modal = activeModal;
    const opening = !modal;
    if (opening) {
      modal = document.createElement('dialog');
      modal.className = 'count-upgrade-popup count-upgrade-popup--modal';
      modal.setAttribute('aria-labelledby', 'CountUpgradeModalTitle');
      modal.setAttribute('tabindex', '-1');
      parent.append(modal);
      activeModal = modal;
      modal.addEventListener('keydown', event => event.stopPropagation());
      modal.addEventListener('close', () => {
        modal.remove();
        if (activeModal === modal) activeModal = null;
        const selected = parent.querySelector('input:checked, .is-selected, select');
        if (selected?.isConnected) selected.focus({ preventScroll: true });
      }, { once: true });
    }
    const markup = popupMarkup(offer, money, quantity, true);
    if (modal.dataset.markup !== markup) {
      modal.innerHTML = markup;
      modal.dataset.markup = markup;
      modal.querySelector('h2').id = 'CountUpgradeModalTitle';
      const decline = document.createElement('button');
      decline.type = 'button';
      decline.className = 'count-upgrade-popup__decline';
      decline.dataset.modalDismiss = '';
      decline.textContent = 'No thanks';
      modal.querySelector('.count-upgrade-popup__body').append(decline);
    }
    modal.onclick = event => {
      event.stopPropagation();
      if (event.target.closest('[data-upgrade-accept]')) {
        activeModal = null;
        modal.close();
        onAccept?.();
      } else if (event.target.closest('[data-modal-dismiss]') || event.target === modal) {
        modal.close();
      }
    };
    if (opening) {
      modal.showModal();
      modal.focus({ preventScroll: true });
    }
  }

  let activePopup;
  function dismissPopup(parent) {
    if (activeModal && (!parent || parent.contains(activeModal))) {
      activeModal.close();
      activeModal = null;
    }
    if (!parent || parent.contains(activePopup)) {
      activePopup?.remove();
      activePopup = null;
    }
  }

  function showPopup(offer, money, { quantity = 1, parent, onAccept, allowPopup = false } = {}) {
    if (!offer || !parent) {
      dismissPopup(parent);
      return;
    }
    if (activePopup?.parentElement !== parent) {
      dismissPopup();
      activePopup = document.createElement('div');
      activePopup.className = 'count-upgrade-popup';
      activePopup.setAttribute('role', 'region');
      activePopup.setAttribute('aria-label', 'Count upgrade offer');
      parent.append(activePopup);
    }
    const markup = popupMarkup(offer, money, quantity);
    if (activePopup.innerHTML !== markup) activePopup.innerHTML = markup;
    activePopup.onclick = event => {
      event.stopPropagation();
      if (event.target.closest('[data-upgrade-accept]')) onAccept?.();
    };
    if (activeModal?.parentElement === parent || (allowPopup && claimPopup())) {
      showFullPopup(offer, money, { quantity, parent, onAccept });
    }
  }

  window.DubiaCountUpgrade = { defaultVariant, findOffer, render, popupMarkup, showPopup, dismissPopup, claimPopup, optionPrice, renderOptionPrice, alignCountOffers, settleCountOffers, toggleOptionSection, updateOptionToggles, revealSelectedOption, stopOptionScroll };

  class ProductCountUpgrade extends HTMLElement {
    connectedCallback() {
      this.scope = this.closest('.product__info-container');
      this.form = document.getElementById(this.dataset.form);
      this.picker = this.scope?.querySelector('variant-radios, variant-selects');
      if (!this.form || !this.picker) return;
      this.product = { options: JSON.parse(this.dataset.options), variants: this.picker.getVariantData() };
      this.refresh = this.refresh.bind(this);
      this.onChange = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(this.refresh);
      };
      this.applyUpgrade = () => {
        this.refresh();
        if (!this.offer) return;
        const target = options(this.offer.variant)[this.offer.optionIndex];
        const groups = this.picker.querySelectorAll('fieldset');
        let control;
        if (groups.length) {
          control = [...groups[this.offer.optionIndex].querySelectorAll('input[type="radio"]')].find(input => input.value === target);
          if (control) control.checked = true;
        } else {
          control = this.picker.querySelectorAll('select')[this.offer.optionIndex];
          if (control) control.value = target;
        }
        if (control) {
          this.upgradeAccepted = true;
          this.offerShown = false;
          cancelAnimationFrame(this.popupFrame);
          dismissPopup(this.picker);
          control.dispatchEvent(new Event('change', { bubbles: true }));
          this.refresh();
          control.focus({ preventScroll: true });
        }
      };
      this.onClick = event => {
        if (event.target.closest('[data-count-upgrade]')) this.applyUpgrade();
      };
      this.scope.addEventListener('change', this.onChange);
      this.scope.addEventListener('input', this.onChange);
      this.onOptionsToggle = event => {
        const button = event.target.closest('[data-product-options-toggle]');
        if (button) toggleOptionSection(button);
      };
      this.onCountSelection = event => {
        const control = event.target;
        if (!(event.type === 'click' && control.matches('input[type="radio"]')) &&
            !(event.type === 'change' && event.isTrusted && control.matches('select'))) return;
        const groups = [...this.picker.querySelectorAll('fieldset')];
        const index = groups.length ? groups.indexOf(control.closest('fieldset')) : [...this.picker.querySelectorAll('select')].indexOf(control);
        cancelAnimationFrame(this.popupFrame);
        this.popupFrame = requestAnimationFrame(() => {
          this.refresh();
          const offer = this.offer;
          if (!offer || offer.optionIndex !== index) return;
          this.upgradeAccepted = false;
          this.offerShown = true;
          this.refresh();
          this.showCurrentOffer(true);
        });
      };
      this.picker.addEventListener('click', this.onCountSelection);
      this.picker.addEventListener('change', this.onCountSelection);
      this.picker.addEventListener('click', this.onOptionsToggle);
      this.onOptionScroll = () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => settleCountOffers(this.picker));
      };
      this.picker.addEventListener('scroll', this.onOptionScroll, { capture: true, passive: true });
      this.scope.addEventListener('click', this.onClick);
      // Recharge updates hidden inputs silently, then updates the cart button label.
      this.observer = new MutationObserver(this.onChange);
      this.observer.observe(this.form, { attributes: true, childList: true, characterData: true, subtree: true });
      this.resizeObserver = new ResizeObserver(() => updateOptionToggles(this.picker));
      this.resizeObserver.observe(this.picker);
      this.refresh();
    }

    showCurrentOffer(allowPopup = false) {
      if (!this.offer || this.upgradeAccepted) {
        dismissPopup(this.picker);
        return;
      }
      const index = this.offer.optionIndex;
      const parent = this.picker.querySelectorAll('fieldset')[index] || this.picker.querySelectorAll('select')[index]?.closest('.product-form__input');
      const money = cents => window.formatProductAddButtonMoney ? window.formatProductAddButtonMoney(cents, this.dataset.moneyFormat) : '$' + (cents / 100).toFixed(2);
      showPopup(this.offer, money, {
        quantity: this.scope.querySelector('[name="quantity"]')?.value,
        parent,
        allowPopup,
        onAccept: () => this.applyUpgrade()
      });
    }

    refresh() {
      const selected = [...this.picker.querySelectorAll('fieldset')].map(group => group.querySelector('input:checked')?.value);
      if (!selected.length) selected.push(...[...this.picker.querySelectorAll('select')].map(select => select.value));
      const variant = this.product.variants.find(item => options(item).every((value, index) => value === selected[index]));
      const planId = this.form.querySelector('[name="selling_plan"]')?.value || '';
      const offerKey = [variant?.id, planId, this.scope.querySelector('[name="quantity"]')?.value].join(':');
      const offerChanged = this.offerKey !== offerKey;
      this.offerKey = offerKey;
      this.offer = findOffer(this.product, variant, planId);
      const money = cents => window.formatProductAddButtonMoney ? window.formatProductAddButtonMoney(cents, this.dataset.moneyFormat) : (cents / 100).toFixed(2);
      this.picker.querySelectorAll('fieldset').forEach((group, index) => {
        const selectedLabel = group.querySelector('[data-selected-option]');
        if (selectedLabel && selectedLabel.textContent !== selected[index]) selectedLabel.textContent = selected[index] || '';
        group.querySelectorAll('input[type="radio"]').forEach(input => {
          const target = input.nextElementSibling?.querySelector('[data-option-price]');
          if (!target) return;
          const pricing = optionPrice(this.product, selected, index, input.value, planId);
          const label = input.nextElementSibling.querySelector('.product-option-value');
          if (label) label.textContent = pricing?.label || input.value;
          const markup = renderOptionPrice(pricing, money);
          if (target.innerHTML !== markup) target.innerHTML = markup;
        });
        const slot = group.querySelector('[data-count-upgrade-slot]');
        const offerHTML = !this.upgradeAccepted && this.offer?.optionIndex === index
          ? render(this.offer, money, this.scope.querySelector('[name="quantity"]')?.value) : '';
        if (slot.innerHTML !== offerHTML) slot.innerHTML = offerHTML;
        slot.hidden = !offerHTML;
        if (this.lastSelectedOptions?.[index] !== selected[index]) {
          const row = group.classList.contains('is-expanded') ? null : group.querySelector('.product-option-values');
          revealSelectedOption(row, Boolean(this.lastSelectedOptions));
        }
      });
      updateOptionToggles(this.picker);
      this.lastSelectedOptions = selected.slice();
      // Native dropdown pickers retain the same text action beneath their select.
      const hasChoices = this.picker.querySelector('.product-option-choice');
      const html = hasChoices || this.upgradeAccepted ? '' : render(this.offer, money, this.scope.querySelector('[name="quantity"]')?.value);
      if (this.innerHTML !== html) this.innerHTML = html;
      this.hidden = !html;
      if (this.offerShown && offerChanged) this.showCurrentOffer();
    }

    disconnectedCallback() {
      dismissPopup(this.picker);
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.popupFrame);
      this.picker?.removeEventListener('click', this.onCountSelection);
      this.picker?.removeEventListener('change', this.onCountSelection);
      cancelAnimationFrame(this.scrollFrame);
      this.picker?.removeEventListener('scroll', this.onOptionScroll, true);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.scope?.removeEventListener('change', this.onChange);
      this.scope?.removeEventListener('input', this.onChange);
      this.picker?.removeEventListener('click', this.onOptionsToggle);
      this.scope?.removeEventListener('click', this.onClick);
    }
  }
  customElements.define('product-count-upgrade', ProductCountUpgrade);
})();
